import { action, makeObservable, observable, runInAction, computed } from 'mobx';
import {
  DEFAULT_ACCENTS,
  isAccents,
  type AccentsType,
  type IAppearancePreferences,
} from '@featit/shared/brand/colour-accents';
import {
  failureToSay,
  type HeldSentenceType,
  pageWords,
  sayFailure,
} from '@/services/actionFailure';
import { expectRecord, UnreadableAnswerError } from '@/services/answerShape';

/**
 * F-0579 ACC-1 (`docs/concept-colour-accents.md`): the device's cached copy of the person's
 * *Accents*, which `index.html`'s pre-paint script reads, so no frame is drawn in the other look.
 */
export const ACCENTS_STORAGE_KEY = 'featit:accents';

/**
 * F-0581 OB-14 (`docs/concept-get-started.md`): a choice made with nobody signed in — Get started's
 * step 1 comes before the account — kept until it is saved to the person who signs in next. Kept in
 * storage, not only in memory, so a reload or a sign-in that leaves the page does not lose it.
 */
export const ACCENTS_UNSAVED_KEY = 'featit:accents:unsaved';

/** The root attribute every accent rule in `index.css` reads: `[data-accents='colour']`. */
export const ACCENTS_ATTRIBUTE = 'accents';

export interface IAppearanceApi {
  appearance(): Promise<IAppearancePreferences>;
  updateAppearance(accents: AccentsType): Promise<IAppearancePreferences>;
}

type StorageType = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/**
 * F-0579 ACC-1: *Accents*, Quiet or Colour — a person's setting, kept with the person on the server
 * (`users.preferences.appearance`) so every device they sign in on draws the same look, and cached
 * on the device for the pre-paint script. The cache is a person store under the owner rule
 * (`storeRegister.ts`), so the next person to sign in here does not inherit it.
 *
 * Choosing applies at once and is then saved; a refused save puts the last look back and says so.
 * An answer from the server that arrives after a choice made here is not applied over it. A choice
 * made with nobody signed in is saved to whoever signs in next, in place of the server's (OB-14).
 */
export class AppearanceStore {
  accents: AccentsType;
  saving = false;
  /** Why the setting could not be read or saved, for the Settings screen. */
  heldError: HeldSentenceType | null = null;
  get error(): string | null {
    return sayFailure(pageWords(), this.heldError);
  }
  set error(value: HeldSentenceType | null) {
    this.heldError = value;
  }
  private loadedFor: string | null = null;
  /** Bumped by every choice made here, so a read already on its way does not undo it. */
  private choices = 0;
  /** OB-14: chosen with nobody signed in, not yet saved to a person. */
  private unsaved: AccentsType | null;

  constructor(
    private readonly options: {
      userId: () => string | null;
      api: IAppearanceApi;
      storage: () => StorageType | null;
      root: () => HTMLElement | null;
    },
  ) {
    this.accents = this.cached() ?? DEFAULT_ACCENTS;
    this.unsaved = this.read(ACCENTS_UNSAVED_KEY);
    this.apply();
    makeObservable<AppearanceStore, 'adopt'>(this, {
      accents: observable,
      saving: observable,
      heldError: observable.ref,
      error: computed,
      adopt: action,
      setAccents: action,
    });
  }

  /** The signed-in person's setting, once per person; a failed read keeps the device's copy. */
  async load(): Promise<void> {
    const userId = this.options.userId();
    if (!userId || this.loadedFor === userId) return;
    this.loadedFor = userId;
    if (this.unsaved) {
      await this.saveUnsaved(userId, this.unsaved);
      return;
    }
    const choices = this.choices;
    try {
      const preferences = expectRecord<IAppearancePreferences>(
        await this.options.api.appearance(),
        'Your appearance setting',
        ['accents'],
      );
      if (!isAccents(preferences.accents)) {
        throw new UnreadableAnswerError('Your appearance setting');
      }
      if (this.loadedFor !== userId || this.options.userId() !== userId) return;
      if (this.choices !== choices) return;
      this.adopt(preferences.accents);
    } catch {
      if (this.loadedFor === userId) this.loadedFor = null;
    }
  }

  /** The person is someone else now, or nobody: read the setting again for whoever is here. */
  forget(): void {
    this.loadedFor = null;
  }

  async setAccents(accents: AccentsType): Promise<void> {
    if (accents === this.accents) return;
    const previous = this.accents;
    this.choices += 1;
    this.adopt(accents);
    this.error = null;
    if (!this.options.userId()) {
      this.markUnsaved(accents);
      return;
    }
    this.markUnsaved(null);
    this.saving = true;
    try {
      await this.options.api.updateAppearance(accents);
    } catch (error) {
      runInAction(() => {
        if (this.accents === accents) this.adopt(previous);
        this.error = failureToSay(error, 'settingsAccount.appearance.actions.saveAccents');
      });
    } finally {
      runInAction(() => {
        this.saving = false;
      });
    }
  }

  /** OB-14: the choice made before sign-in becomes the person's; a refused save is tried next time. */
  private async saveUnsaved(userId: string, accents: AccentsType): Promise<void> {
    this.adopt(accents);
    try {
      await this.options.api.updateAppearance(accents);
      if (this.unsaved === accents) this.markUnsaved(null);
    } catch {
      if (this.loadedFor === userId) this.loadedFor = null;
    }
  }

  private markUnsaved(accents: AccentsType | null): void {
    this.unsaved = accents;
    try {
      if (accents) this.options.storage()?.setItem(ACCENTS_UNSAVED_KEY, accents);
      else this.options.storage()?.removeItem(ACCENTS_UNSAVED_KEY);
    } catch {
      // Storage unavailable: the choice is still saved at a sign-in on this page.
    }
  }

  private adopt(accents: AccentsType): void {
    this.accents = accents;
    try {
      this.options.storage()?.setItem(ACCENTS_STORAGE_KEY, accents);
    } catch {
      // Storage unavailable: the next launch's first frame draws the default until the read lands.
    }
    this.apply();
  }

  private cached(): AccentsType | null {
    return this.read(ACCENTS_STORAGE_KEY);
  }

  private read(key: string): AccentsType | null {
    try {
      const stored = this.options.storage()?.getItem(key);
      return isAccents(stored) ? stored : null;
    } catch {
      return null;
    }
  }

  private apply(): void {
    const root = this.options.root();
    if (root) root.dataset[ACCENTS_ATTRIBUTE] = this.accents;
  }
}
